import { useContext, useMemo } from 'react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { SECONDS_IN_DAY, SECONDS_IN_HOUR } from '@cast/constants';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { WorkloadDetailsContext } from '@cast/split-me/features/cluster/workload-optimization';
import { useWorkloadDataTransferCostQuery } from '@cast/split-me/hooks';

import { useInventoryWorkloadsTimeRangeSelection } from '../../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

dayjs.extend(utc);
dayjs.extend(timezone);

const getStepSeconds = (from: dayjs.Dayjs, to: dayjs.Dayjs): number => {
  const durationSeconds = to.diff(from, 'second');

  return durationSeconds <= SECONDS_IN_DAY ? SECONDS_IN_HOUR : SECONDS_IN_DAY;
};

const alignRange = (
  range: { from: string; to: string },
  timeZone: string,
  stepSeconds: number
): { from: dayjs.Dayjs; to: dayjs.Dayjs } => {
  const unit = stepSeconds === SECONDS_IN_HOUR ? 'hour' : 'day';

  return {
    from: dayjs.tz(range.from, timeZone).startOf(unit).utc(),
    to: dayjs.tz(range.to, timeZone).startOf(unit).add(1, unit).utc(),
  };
};

export const useWorkloadNetworkDataTransfer = () => {
  const { workload } = useContext(WorkloadDetailsContext);
  const { cluster } = useCluster();
  const { getRange, timeZone, key } = useInventoryWorkloadsTimeRangeSelection();

  const range = useMemo(() => getRange(), [key]);
  const stepSeconds = getStepSeconds(
    dayjs.tz(range.from, timeZone),
    dayjs.tz(range.to, timeZone)
  );
  const { from, to } = useMemo(
    () => alignRange(range, timeZone, stepSeconds),
    [range, timeZone, stepSeconds]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useWorkloadDataTransferCostQuery({
      clusterId: cluster.id,
      namespace: workload?.namespace ?? '',
      workload: workload?.name ?? '',
      workloadType: workload?.kind ?? '',
      startTime: from.toISOString(),
      endTime: to.toISOString(),
      stepSeconds,
      enabled: !!workload && !!cluster.id,
    });

  const hasWorkload = !!workload && !!cluster.id;

  return {
    data,
    isLoading: isLoading || !hasWorkload,
    isFetching,
    error,
    refetch,
    timeZone,
    stepSeconds,
  };
};
